/**
 * QR codes for invitations (2026-10-21): each guest's card carries a code their phone's
 * camera opens - the link to their own RSVP page (invite.tsx, apps/api/src/events.ts).
 * Drawn as SVG squares, so it prints sharp in the card, on the print page and in the PDF.
 */
import qrcode from 'qrcode-generator';

/** Medium error correction, unless the code would be denser than this many squares across and low makes it smaller. */
const DENSEST_AT_MEDIUM = 37;

function encode(text: string, level: 'L' | 'M') {
  const q = qrcode(0, level);
  q.addData(text);
  q.make();
  return q;
}

/**
 * The code for `text`, as rows of dark (true) and light modules. Medium error correction - a
 * little ink smudge is forgiven - unless the text is long enough (a guest's link with their name,
 * 2026-10-24) that low correction gives bigger squares, which matter more on paper.
 */
export function qrModules(text: string): boolean[][] {
  let q = encode(text, 'M');
  if (q.getModuleCount() > DENSEST_AT_MEDIUM) {
    const low = encode(text, 'L');
    if (low.getModuleCount() < q.getModuleCount()) q = low;
  }
  const n = q.getModuleCount();
  return Array.from({ length: n }, (_, r) => Array.from({ length: n }, (_, c) => q.isDark(r, c)));
}

/** One SVG path drawing every dark module of a code `size` mm wide, from (x, y). */
export function qrPath(modules: boolean[][], x: number, y: number, size: number): string {
  const n = modules.length, m = size / n;
  let d = '';
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      // a run of dark modules in a row is one rectangle: a shorter path, and no hairline seams
      if (!modules[r][c] || (c > 0 && modules[r][c - 1])) continue;
      let run = 1;
      while (c + run < n && modules[r][c + run]) run++;
      d += `M${(x + c * m).toFixed(3)} ${(y + r * m).toFixed(3)}h${(run * m).toFixed(3)}v${m.toFixed(3)}h${(-run * m).toFixed(3)}z`;
    }
  }
  return d;
}
